import type { Entry, Section } from "@schema/catalog.ts";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useProgressStore } from "@/state/progress.ts";
import { EntryCard } from "./EntryCard.tsx";
import styles from "./PartSection.module.css";

interface Props {
  section: Section;
  entries: Entry[];
  /** Entries in this part before filtering, so the header can own up to what it's hiding. */
  total: number;
}

/**
 * One part of the road. Carries data-phase — the IntersectionObserver in
 * usePhaseTheme watches these elements to decide the active phase.
 */
export function PartSection({ section, entries, total }: Props) {
  const catalog = useCatalog();
  const watchedHere = useProgressStore((s) =>
    entries.reduce((n, e) => n + (s.watched[e.id] ? 1 : 0), 0),
  );
  const phase = catalog.phases.find((p) => p.id === section.phaseId);
  const saga = phase && catalog.sagas.find((s) => s.id === phase.sagaId);
  const headingId = `${section.id}-heading`;

  return (
    <section className={styles.section} data-phase={section.phaseId} aria-labelledby={headingId}>
      <header className={styles.head}>
        {phase && saga && (
          <p className={styles.kicker}>
            {saga.title} · {phase.title}
          </p>
        )}
        <h2 id={headingId} className={styles.title}>
          {section.title}
          {entries.length < total && (
            <span className={styles.count}>
              {entries.length} of {total}
            </span>
          )}
        </h2>
        <p className={styles.subtitle}>{section.blurb}</p>
        {entries.length > 0 && (
          <p className={styles.progress} data-done={watchedHere === entries.length}>
            <span
              className={styles.progressBar}
              aria-hidden="true"
              style={
                {
                  "--fill": `${Math.round((watchedHere / entries.length) * 100)}%`,
                } as React.CSSProperties
              }
            />
            {watchedHere === entries.length
              ? "Part complete"
              : `${watchedHere} of ${entries.length} watched`}
          </p>
        )}
      </header>
      <ol className={styles.entries}>
        {entries.map((entry) => (
          <li key={entry.id} className={styles.entryItem}>
            <EntryCard entry={entry} />
          </li>
        ))}
      </ol>
    </section>
  );
}
